import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Scale, 
  Calculator, 
  Users, 
  Sparkles,
  ArrowRight,
  Download,
  ShieldCheck,
  BookOpen,
  BadgeCheck,
  TrendingUp,
  Menu,
  X,
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  ExternalLink
} from 'lucide-react';
import './LandingPage.css';

// --- LOGIC AUTH ---
const useAuth = () => {
  return !!localStorage.getItem("token");
};

// Navigation Component
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isLoggedIn = useAuth(); // Cek Status Login

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`tacia-nav ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/" className="nav-logo" style={{ textDecoration: 'none' }}>
            <img src="/tacia-icon.png" alt="Tacia Icon" className="h-9 w-9 object-contain" />
            <img src="/tacia-text.png" alt="Tacia" className="h-6 object-contain" />
          </Link>
          <a
            href="https://enforcea.com"
            target="_blank"
            rel="noopener noreferrer"
            className="desktop-only"
            style={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: 'rgba(196, 30, 36, 0.08)',
              color: '#C41E24',
              border: '1px solid rgba(196, 30, 36, 0.2)',
              transition: 'all 0.2s',
            }}
            title="Mitra Ahli Hukum Perpajakan: EnforceA (enforcea.com)"
          >
            <span style={{ opacity: 0.7, fontWeight: 400 }}>with</span>
            <span>EnforceA</span>
            <ExternalLink size={10} />
          </a>
        </div>
        
        <div className="nav-links desktop-only">
          <a href="#features">Fitur</a>
          <a href="#pricing">Harga</a>
          <a href="#about">Tentang</a>
        </div>
        
        <div className="nav-actions">
          {isLoggedIn ? (
            <Link to="/dashboard" className="btn-primary">
              Buka Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="btn-text">
                Masuk
              </Link>
              <Link to="/signup" className="btn-primary">
                Daftar Sekarang
              </Link>
            </>
          )}

          <button 
            className="mobile-menu-btn mobile-only"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <a href="#features" onClick={() => setMobileMenuOpen(false)}>Fitur</a>
          <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Harga</a>
          <a href="#about" onClick={() => setMobileMenuOpen(false)}>Tentang</a>
          {/* Menu Mobile juga perlu logic auth */}
          {isLoggedIn ? (
             <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} style={{fontWeight: 'bold', color: '#3273D9'}}>
               Dashboard
             </Link>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Masuk</Link>
              <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>Daftar</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

// Hero Section
function HeroSection() {
  const [visible, setVisible] = useState(false);
  const isLoggedIn = useAuth(); // Cek Status Login

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="hero-section">
      {/* Background Blobs */}
      <div className="blob blob-mint" style={{ left: '8vw', top: '10vh', width: '34vw', height: '34vw', animationDelay: '0s' }} />
      <div className="blob blob-lavender" style={{ right: '6vw', bottom: '12vh', width: '30vw', height: '30vw', animationDelay: '-5s' }} />
      <div className="blob blob-blush" style={{ left: '35vw', top: '60vh', width: '25vw', height: '25vw', opacity: 0.4, animationDelay: '-10s' }} />

      <div className="hero-content">
        {/* Badge */}
        <div className={`hero-badge ${visible ? 'visible' : ''}`}>
          <span className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ position: 'relative', display: 'flex', width: '8px', height: '8px' }}>
              <span style={{ position: 'absolute', width: '100%', height: '100%', borderRadius: '50%', background: '#10B981', opacity: 0.75, animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
              <span style={{ position: 'relative', width: '8px', height: '8px', borderRadius: '50%', background: '#10B981' }} />
            </span>
            Konsultan Pajak & Coretax AI Indonesia
          </span>
        </div>

        {/* Headline */}
        <div className={`hero-text ${visible ? 'visible' : ''}`}>
          <h1>AI Konsultan Pajak, Coretax & Legal Bisnis</h1>
          <p className="hero-subtitle">
            Asisten cerdas perpajakan Indonesia. Riset regulasi terverifikasi, otomatisasi draf dokumen resmi, hingga konversi instan Excel ke XML Coretax DJP secara presisi.
          </p>
        </div>

        {/* Hero Card - Real Consultation & Coretax Preview */}
        <div className={`hero-card glass-card ${visible ? 'visible' : ''}`}>
          {/* Card Window Header */}
          <div className="card-header" style={{ padding: '16px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <div className="card-header-left" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src="/tacia-icon.png" alt="Tacia Icon" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
              <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
                  Tacia Tax Intelligence
                </span>
                <span style={{ fontSize: '11px', color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#059669', display: 'inline-block' }}></span>
                  Coretax DJP Ready
                </span>
              </div>
            </div>
            <div className="card-tags" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span className="tag tag-primary" style={{ fontSize: '11px', padding: '4px 10px', fontWeight: 600 }}>PPh 21 TER</span>
              <span className="tag" style={{ fontSize: '11px', padding: '4px 10px', color: '#334155' }}>Bupot Unifikasi</span>
              <span className="tag" style={{ fontSize: '11px', padding: '4px 10px', color: '#334155' }}>SPT Badan</span>
            </div>
          </div>

          {/* Interactive Consultation Preview Body */}
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
            
            {/* User Query Bubble */}
            <div style={{ alignSelf: 'flex-end', maxWidth: '88%' }}>
              <div style={{ fontSize: '11px', color: '#475569', fontWeight: 600, marginBottom: '4px', textAlign: 'right' }}>
                Wajib Pajak Badan
              </div>
              <div style={{
                background: '#EBF2FC',
                border: '1px solid #BFDBFE',
                borderRadius: '16px 16px 4px 16px',
                padding: '12px 16px',
                fontSize: '13px',
                color: '#0F172A',
                fontWeight: 500,
                lineHeight: 1.55
              }}>
                Tolong hitung PPh 21 skema TER bulanan karyawan dan siapkan file XML Bukti Potong Coretax dari data Excel terlampir.
              </div>
            </div>

            {/* Tacia Response Bubble */}
            <div style={{
              alignSelf: 'flex-start',
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderLeft: '4px solid #3273D9',
              borderRadius: '4px 16px 16px 16px',
              padding: '16px 18px',
              maxWidth: '98%',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img src="/tacia-icon.png" alt="Tacia" style={{ width: '18px', height: '18px', objectFit: 'contain' }} />
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>Tacia AI</span>
                </div>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#047857', background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '3px 8px', borderRadius: '6px' }}>
                  PMK 168/2023 & UU HPP Terverifikasi
                </span>
              </div>

              <div style={{ fontSize: '13px', color: '#1E293B', lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#3273D9' }} />
                  <span style={{ color: '#1E293B' }}><strong style={{ color: '#0F172A' }}>Kalkulasi TER Presisi:</strong> Tarif efektif Kategori A, B, dan C diterapkan otomatis sesuai PTKP tanpa selisih hitung.</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <FileSpreadsheet className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#3273D9' }} />
                  <span style={{ color: '#1E293B' }}><strong style={{ color: '#0F172A' }}>Generator XML Coretax:</strong> Validasi skema XSD resmi DJP untuk 10 template Bupot & 8 lampiran SPT Badan.</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <FileText className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#3273D9' }} />
                  <span style={{ color: '#1E293B' }}><strong style={{ color: '#0F172A' }}>Draf Dokumen Hukum:</strong> Siap ekspor surat keberatan, tanggapan SP2DK, dan permohonan angsuran resmi (Word).</span>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginTop: '6px', paddingTop: '16px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {isLoggedIn ? (
                  <Link to="/dashboard" className="btn-primary" style={{ padding: '12px 24px', fontSize: '13px' }}>
                    Buka Dashboard Konsultasi
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <>
                    <Link to="/signup" className="btn-primary" style={{ padding: '12px 24px', fontSize: '13px' }}>
                      Mulai Konsultasi Gratis
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/login" className="btn-secondary" style={{ padding: '12px 20px', fontSize: '13px', color: '#0F172A', background: '#FFFFFF', border: '1px solid #CBD5E1', fontWeight: 600 }}>
                      Masuk Akun
                    </Link>
                  </>
                )}
              </div>
              <div style={{ fontSize: '11.5px', color: '#475569', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#475569', fontWeight: 500 }}>
                  <ShieldCheck className="w-3.5 h-3.5" style={{ color: '#3273D9' }} />
                  Sumber JDIH Resmi
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#475569', fontWeight: 500 }}>
                  <BadgeCheck className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                  Coretax XML Valid
                </span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

// Feature Section Component
function FeatureSection({ 
  title, 
  subtitle, 
  cardTitle, 
  items,
  direction = 'right',
  icon: Icon,
  stats,
  id
}) {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id={id} className="feature-section">
      {/* Background Blobs */}
      <div className="blob blob-mint" style={{ left: '5vw', top: '15vh', width: '28vw', height: '28vw', opacity: 0.5, animationDelay: '-3s' }} />
      <div className="blob blob-lavender" style={{ right: '8vw', bottom: '10vh', width: '32vw', height: '32vw', opacity: 0.45, animationDelay: '-8s' }} />

      <div className="feature-content">
        <div className={`feature-header ${visible ? 'visible' : ''}`}>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>

        <div className="feature-cards">
          <div className={`glass-card left-card ${visible ? 'visible' : ''}`}>
            <div className="card-header">
              <div className="card-header-left">
                <div className="icon-box">
                  <Sparkles className="w-4 h-4" />
                </div>
                <span>Tacia AI</span>
              </div>
              {stats && (
                <span className="tag tag-success">
                  {stats.value} {stats.label}
                </span>
              )}
            </div>
            <div className="card-content">
              {/* Dummy Items for decorative purpose only in static card */}
              <div className="list-item">
                <div className="list-icon bg-blue"><BookOpen className="w-4 h-4" /></div>
                <div className="list-text">
                  <div className="list-title">UU Cipta Kerja No. 6/2023</div>
                  <div className="list-subtitle">Referensi valid • Diperbarui 2 hari lalu</div>
                </div>
              </div>
              <div className="list-item">
                <div className="list-icon bg-green"><Scale className="w-4 h-4" /></div>
                <div className="list-text">
                  <div className="list-title">PMK No. 168/2023</div>
                  <div className="list-subtitle">Peraturan Pajak Terbaru</div>
                </div>
              </div>
              <div className="list-item">
                <div className="list-icon bg-yellow"><TrendingUp className="w-4 h-4" /></div>
                <div className="list-text">
                  <div className="list-title">BPJS Ketenagakerjaan</div>
                  <div className="list-subtitle">Perhitungan iuran terkini</div>
                </div>
              </div>
            </div>
          </div>

          <div className={`glass-card right-card ${visible ? 'visible' : ''}`}>
            <div className="card-header">
              <div className="card-header-left">
                <div className="icon-box">
                  <Icon className="w-4 h-4" />
                </div>
                <span>{cardTitle}</span>
              </div>
              <span className="tag tag-primary">AI Verified</span>
            </div>
            <div className="card-content">
              {items.map((item, idx) => (
                <div key={idx} className="list-item">
                  <div className={`list-icon ${item.verified ? 'bg-green' : 'bg-gray'}`}>
                    {item.verified ? (
                      <BadgeCheck className="w-4 h-4" />
                    ) : (
                      <div className="check-circle" />
                    )}
                  </div>
                  <div className="list-text">
                    <div className="list-title">{item.title}</div>
                    <div className="list-subtitle">{item.subtitle}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Daily Sync Section
function DailySyncSection() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const isLoggedIn = useAuth(); // Cek Status Login

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="sync-section">
      <div className="blob blob-mint" style={{ left: '10vw', top: '20vh', width: '26vw', height: '26vw', opacity: 0.5 }} />
      <div className="blob blob-lavender" style={{ right: '10vw', bottom: '15vh', width: '30vw', height: '30vw', opacity: 0.45 }} />
      <div className="blob blob-blush" style={{ left: '40vw', top: '60vh', width: '22vw', height: '22vw', opacity: 0.4 }} />

      <div className="sync-content">
        <div className={`sync-header ${visible ? 'visible' : ''}`}>
          <h2>Satu Platform, Semua Solusi.</h2>
          <p>Tacia mengintegrasikan HR, Pajak, dan Legal dalam satu AI konsultan.</p>
        </div>

        <div className="sync-cards">
          <div className={`glass-card main-card ${visible ? 'visible' : ''}`}>
            <div className="card-header">
              <span>Tacia AI Consultant</span>
              <span className="tag tag-success">AI Powered</span>
            </div>
            <div className="card-body center">
              <div className="card-icon-large">
                <Sparkles className="w-7 h-7" />
              </div>
              <h3>Apa yang bisa Tacia bantu?</h3>
              <p>
                Tanya tentang perhitungan PPh 21, BPJS, UU Ketenagakerjaan, atau draft kontrak kerja.
              </p>
              
              <div className="card-actions">
                {isLoggedIn ? (
                   <Link to="/dashboard" className="btn-primary">Mulai Konsultasi</Link>
                ) : (
                   <Link to="/signup" className="btn-primary">Mulai Konsultasi</Link>
                )}
                <button className="btn-text">Lihat Contoh</button>
              </div>
            </div>
          </div>
          {/* Decorative small cards */}
          <div className={`glass-card-small card-hr ${visible ? 'visible' : ''}`}>
            <div className="small-icon bg-blue"><Users className="w-5 h-5" /></div>
            <div className="small-title">HR Management</div>
            <div className="small-subtitle">Cuti, THR, PHK</div>
          </div>
          <div className={`glass-card-small card-tax ${visible ? 'visible' : ''}`}>
            <div className="small-icon bg-green"><Calculator className="w-5 h-5" /></div>
            <div className="small-title">Pajak Indonesia</div>
            <div className="small-subtitle">PPh 21, PPN, SPT</div>
          </div>
          <div className={`glass-card-small card-legal ${visible ? 'visible' : ''}`}>
            <div className="small-icon bg-yellow"><Scale className="w-5 h-5" /></div>
            <div className="small-title">Legal & UU</div>
            <div className="small-subtitle">Kontrak, Regulasi</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Final Composition Section
function FinalCompositionSection() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const isLoggedIn = useAuth(); // Cek Login

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="dashboard-section full-center">
      <div className="blob blob-mint" style={{ left: '5vw', top: '10vh', width: '28vw', height: '28vw', opacity: 0.45 }} />
      <div className="blob blob-lavender" style={{ right: '5vw', bottom: '10vh', width: '32vw', height: '32vw', opacity: 0.4 }} />
      <div className="blob blob-blush" style={{ left: '40vw', top: '65vh', width: '24vw', height: '24vw', opacity: 0.35 }} />

      <div className="dashboard-content center-wrapper">
        <div className="dashboard-panels">
          {/* Sidebar Dummy */}
          <div className={`glass-card sidebar ${visible ? 'visible' : ''}`}>
            <div className="sidebar-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src="/tacia-icon.png" alt="Tacia" className="h-6 w-6 object-contain" />
              <img src="/tacia-text.png" alt="Tacia" className="h-4 object-contain" />
            </div>
            <nav className="sidebar-nav">
              <button className="nav-item active"><Users className="w-4 h-4" /> HR</button>
              <button className="nav-item"><Calculator className="w-4 h-4" /> Pajak</button>
              <button className="nav-item"><Scale className="w-4 h-4" /> Legal</button>
              <button className="nav-item"><BookOpen className="w-4 h-4" /> Regulasi</button>
            </nav>
          </div>

          {/* Main Panel */}
          <div className={`glass-card main-panel ${visible ? 'visible' : ''}`}>
            <div className="card-header">
              <span>Riwayat Konsultasi</span>
              <span className="text-muted">Hari ini</span>
            </div>
            <div className="card-content">
              <div className="consultation-list">
                <div className="consultation-item">
                  <div className="list-icon bg-blue"><Calculator className="w-4 h-4" /></div>
                  <div className="list-text">
                    <div className="list-title">Perhitungan PPh 21 Karyawan Baru</div>
                    <div className="list-subtitle">Referensi: PMK-168/2023 • 10:30 AM</div>
                  </div>
                  <BadgeCheck className="w-4 h-4 text-green" />
                </div>
              </div>
              <div className="panel-footer">
                <p className="text-muted">Siap menghemat waktu dan biaya konsultan?</p>
                <div className="panel-actions">
                  {isLoggedIn ? (
                     <Link to="/dashboard" className="btn-primary">Buka Dashboard</Link>
                  ) : (
                     <Link to="/signup" className="btn-primary">Coba Gratis 14 Hari</Link>
                  )}
                  <button className="btn-secondary">
                    <Download className="w-4 h-4" /> Download
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={`glass-card right-panel ${visible ? 'visible' : ''}`}>
            <div className="card-header">
              <span>Update Regulasi</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Footer Section
function FooterSection() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <footer ref={sectionRef} className="footer-section">
      <div className="blob blob-lavender" style={{ left: '-10vw', top: '20%', width: '35vw', height: '35vw', opacity: 0.3 }} />
      <div className="blob blob-mint" style={{ right: '-5vw', bottom: '10%', width: '30vw', height: '30vw', opacity: 0.25 }} />

      <div className="footer-content">
        <div className={`footer-newsletter ${visible ? 'visible' : ''}`}>
          <h2>Tetap update regulasi terbaru.</h2>
          <p>Dapatkan notifikasi perubahan UU, PMK, dan aturan terbaru langsung di email.</p>
          <div className="newsletter-form">
            <input type="email" placeholder="Email address" className="newsletter-input" />
            <button className="btn-primary">Subscribe</button>
          </div>
        </div>

        <div className={`footer-links ${visible ? 'visible' : ''}`}>
          <div className="footer-column">
            <h4>Produk</h4>
            <ul>
              <li><a href="/">HR AI</a></li>
              <li><a href="/">Tax Calculator</a></li>
              <li><a href="/">Legal Assistant</a></li>
            </ul>
          </div>
          <div className="footer-column">
            <h4>Perusahaan</h4>
            <ul>
              <li><a href="/">Tentang Kami</a></li>
              <li><a href="/">Kontak</a></li>
            </ul>
          </div>
          <div className="footer-column">
            <h4>Support</h4>
            <ul>
              <li><a href="/">Help Center</a></li>
              <li><a href="/">Kebijakan Privasi</a></li>
            </ul>
          </div>
        </div>

        <div className={`footer-bottom ${visible ? 'visible' : ''}`}>
          <div className="footer-logo" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/tacia-icon.png" alt="Tacia Icon" className="h-6 w-6 object-contain" />
            <img src="/tacia-text.png" alt="Tacia" className="h-4 object-contain" />
          </div>
          <p>
            © 2026 Tacia. AI Konsultan Perpajakan & Legal Bisnis Indonesia.
            <span style={{ margin: '0 8px', color: '#DEE4DC' }}>|</span>
            <span style={{ color: '#7A8A85' }}>Teknologi oleh Wibi Digital</span>
            <span style={{ margin: '0 6px', color: '#7A8A85' }}>•</span>
            <span style={{ color: '#7A8A85' }}>Didukung Ahli Hukum</span>{' '}
            <a
              href="https://enforcea.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#C41E24', fontWeight: 700, textDecoration: 'none' }}
              className="hover:underline"
            >
              EnforceA
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

// Main Landing Page Component
function LandingPage() {
  return (
    <div className="landing-page">
      <div className="noise-overlay" />
      <Navigation />
      <main>
        <HeroSection />
        
        <FeatureSection 
          id="features"
          title="HR yang Selalu Update"
          subtitle="Ketentuan cuti, THR, PHK, BPJS—dengan referensi UU terbaru."
          cardTitle="Konsultasi HR"
          icon={Users}
          direction="right"
          stats={{ label: "UU Terbaru", value: "100%" }}
          items={[
            { title: 'Ketentuan Cuti Tahunan 12 Hari', subtitle: 'UU Cipta Kerja No. 6/2023 • Terverifikasi', verified: true },
            { title: 'Perhitungan THR Pro-rata', subtitle: 'Referensi: Kemenaker • Valid', verified: true },
            { title: 'BPJS Ketenagakerjaan 2024', subtitle: 'Iuran JKK, JKM, JHT terkini', verified: true },
          ]}
        />
        
        <FeatureSection 
          title="Pajak Indonesia, Presisi 100%"
          subtitle="PPh 21, PPN, SPT—dihitung sesuai PMK dan UU terbaru."
          cardTitle="Kalkulasi Pajak"
          icon={Calculator}
          direction="left"
          stats={{ label: "Akurasi", value: "99.9%" }}
          items={[
            { title: 'PPh 21 Karyawan (PTKP 2024)', subtitle: 'PMK-168/2023 • Terbaru', verified: true },
            { title: 'PPN 11% Barang & Jasa', subtitle: 'HPP Law • Validated', verified: true },
            { title: 'SPT Tahunan Badan', subtitle: 'Formulir & tata cara terkini', verified: true },
          ]}
        />
        
        <FeatureSection 
          title="Legal & Regulasi, Real-Time"
          subtitle="Draft kontrak, review perjanjian, dengan dasar hukum yang kuat."
          cardTitle="Asisten Legal"
          icon={Scale}
          direction="right"
          stats={{ label: "Referensi", value: "1000+" }}
          items={[
            { title: 'Draft Kontrak Kerja (PKWT/PKWTT)', subtitle: 'Sesuai UU Cipta Kerja', verified: true },
            { title: 'NDA & Perjanjian Kerahasiaan', subtitle: 'Template legal-valid', verified: true },
            { title: 'Review Perjanjian Bisnis', subtitle: 'Analisis klausul & risiko', verified: true },
          ]}
        />
        
        <DailySyncSection />
        <FinalCompositionSection />
        <FooterSection />
      </main>
    </div>
  );
}

export default LandingPage;